import React, { useState, useCallback, useEffect } from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import { Col, Row, Modal, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import TextInput from 'components/inputs/Input';
import ReactPagination from 'components/general/ReactPagination';
import Footer from 'components/general/Footer';
import { MdClose, MdClear, MdRefresh, MdFileDownload } from 'react-icons/md';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { BsEye } from 'react-icons/bs';
import CpoSettlementConfigModal from './CpoSettlementConfigModal';
import * as API from 'api/partnerManagement';
import {
  FETCH_CPO_SETTLEMENT_BATCHES,
  REFRESH_CPO_SETTLEMENT_BATCH,
  GET_CPO_SETTLEMENT_BATCH,
  APPROVE_CPO_SETTLEMENT_BATCH,
  REJECT_CPO_SETTLEMENT_BATCH,
  PAY_CPO_SETTLEMENT_BATCH,
  RUN_CPO_SETTLEMENT_CYCLE,
  FETCH_CPO,
} from 'actions/partnerManagement';

/**
 * CDB-1597 — outbound settlement to CPO partners.
 *
 * One row per payment: which partner, on what cycle, how many sessions, how much,
 * the UTR, and what remained outstanding afterwards. Every figure is read as
 * stored on the batch rather than recomputed, so a historical row keeps showing
 * what was true on the day it ran.
 */

const STATUS_LABELS = {
  awaiting_approval: 'Awaiting Approval',
  pending: 'Ready to Pay',
  processing: 'Payment In Progress',
  processed: 'Paid',
  failed: 'Failed',
  reversed: 'Reversed',
  cancelled: 'Rejected',
  needs_review: 'Needs Review',
};

const STATUS_CLASS = {
  awaiting_approval: 'settlement-status--warning',
  pending: 'settlement-status--info',
  processing: 'settlement-status--info',
  processed: 'settlement-status--success',
  failed: 'settlement-status--danger',
  reversed: 'settlement-status--danger',
  cancelled: 'settlement-status--muted',
  needs_review: 'settlement-status--danger',
};

const STATUS_OPTIONS = Object.keys(STATUS_LABELS).map((value) => ({
  label: STATUS_LABELS[value],
  value,
}));

const formatAmount = (value) => (typeof value === 'number' ? value.toFixed(2) : '0.00');

// Same resolution chain used for the batch rows: `name` is never populated in
// either environment, so partner_name leads and partyId is the fallback.
const partnerLabel = (partner) =>
  _.get(partner, 'partner_name') ||
  _.get(partner, 'name') ||
  _.get(partner, 'partyId') ||
  _.get(partner, 'roles[0].business_details.name') ||
  '-';

// Only the last four digits are shown. Enough to confirm the right account is on
// file without putting a full account number on a shared screen.
const maskAccount = (value) => {
  const account = String(value || '');
  return account.length > 4 ? `••••${account.slice(-4)}` : account || '-';
};

// Sentinel for the deliberate "every enabled partner" choice in the run dialog.
// The scheduled daily job does exactly this, so the option has to remain
// reachable — but it is never the default.
const RUN_ALL_PARTNERS = '__all__';

const SettlementList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const batches = useSelector((state) => state.cpoSettlement.batches);
  const isLoader = useSelector((state) => state.cpoSettlement.isLoading);
  const isActionLoading = useSelector((state) => state.cpoSettlement.isActionLoading);
  const refreshingBatchId = useSelector((state) => state.cpoSettlement.refreshingBatchId);
  const isDetailLoading = useSelector((state) => state.cpoSettlement.isDetailLoading);
  const page = useSelector((state) => state.cpoSettlement.page);
  const totalPages = useSelector((state) => state.cpoSettlement.totalPages);
  // Row count, not page count. Passing totalPages as `total` made the footer read
  // "Showing 1-1 of 1" while two rows were on screen.
  const totalRecords = useSelector((state) => state.cpoSettlement.total);
  const batchDetail = useSelector((state) => state.cpoSettlement.batchDetail);
  const batchLines = useSelector((state) => state.cpoSettlement.batchLines);
  const linesPage = useSelector((state) => state.cpoSettlement.linesPage);
  const linesTotalPages = useSelector((state) => state.cpoSettlement.linesTotalPages);
  const linesTotal = useSelector((state) => state.cpoSettlement.linesTotal);
  const reconciles = useSelector((state) => state.cpoSettlement.reconciles);
  const partnerList = useSelector((state) => state.partnerManagement.partnerDetails);

  const [pageNo, setPageNo] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  // Settlement-date window for the batch table. Held as YYYY-MM-DD strings, which
  // is what a native date input reads and writes and what the API accepts.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  // Kept so paging the session breakdown re-requests the same batch.
  const [detailFor, setDetailFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  // One confirmation for both money-moving actions: { batch, kind }. Approving and
  // paying differ only in which request goes out and the wording, so a single
  // dialog keeps the two from drifting apart.
  const [confirmFor, setConfirmFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [editPartnerId, setEditPartnerId] = useState(null);
  const [showRun, setShowRun] = useState(false);
  const [runPartner, setRunPartner] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Which partners are set up for settlement, so "is this partner configured?" is
   * answerable on this screen rather than only by opening the form.
   *
   * The last non-empty list is held locally because opening a partner's Edit form
   * dispatches GET_CPO, and that request clears `partnerDetails` in the shared
   * reducer — which emptied this panel and the run dialog's partner list while the
   * modal was open. Caching here avoids changing reducer behaviour other screens
   * depend on.
   */
  const [loadedPartners, setLoadedPartners] = useState([]);

  useEffect(() => {
    if (_.isArray(partnerList) && partnerList.length) {
      setLoadedPartners(partnerList);
    }
  }, [partnerList]);

  const configuredPartners = React.useMemo(
    () => loadedPartners.filter((partner) => _.get(partner, 'cpo_settlement_config.enabled')),
    [loadedPartners]
  );

  const runPartnerOptions = React.useMemo(
    () => [
      ...configuredPartners.map((partner) => ({ value: partner.id, label: partnerLabel(partner) })),
      { value: RUN_ALL_PARTNERS, label: t('cpoSettlement.runAllPartners') },
    ],
    [configuredPartners, t]
  );

  const fetchBatches = useCallback(
    (data = {}) => {
      dispatch({
        type: FETCH_CPO_SETTLEMENT_BATCHES.REQUEST,
        // `from`/`to` filter settlement_date server-side and are inclusive at both
        // ends, so a single day is expressed by setting both to that day.
        payload: {
          ...data,
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(dateFrom ? { from: dateFrom } : {}),
          ...(dateTo ? { to: dateTo } : {}),
        },
      });
    },
    [dispatch, statusFilter, dateFrom, dateTo]
  );

  const hasFilters = !!(statusFilter || dateFrom || dateTo);

  const clearFilters = useCallback(() => {
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
  }, []);

  useEffect(() => {
    setPageNo(1);
    fetchBatches({ page: 1 });
  }, [fetchBatches]);

  // A high limit rather than pagination: this drives the configured-partners panel
  // and the run dialog, and a partly loaded list would hide configured partners.
  useEffect(() => {
    dispatch({ type: FETCH_CPO.REQUEST, payload: { limit: 999 } });
  }, [dispatch]);

  /**
   * Export every session in the open settlement as a spreadsheet.
   *
   * Requested straight from the API rather than read from the store, because the
   * store only ever holds the page on screen and a partial export would be worse
   * than none — the figures would not add up to the payment. Pages are walked at
   * the API's maximum until the count is satisfied.
   *
   * The charger and station columns carry the partner's own OCPI references, which
   * is what a CPO matches against when a session is queried. Left blank where the
   * partner never sent a CDR for it, rather than filled with a ChargeZone id the
   * partner would not recognise.
   */
  const downloadSessions = useCallback(async () => {
    if (!detailFor) {
      return;
    }
    setIsDownloading(true);
    try {
      const PER_REQUEST = 200;
      const collected = [];
      let current = 1;
      let pages = 1;
      do {
        // Flat payload: the api layer destructures `batchId` off it and sends the
        // rest as query params, so a nested object here would be serialised as one.
        // eslint-disable-next-line no-await-in-loop
        const response = await API.getCpoSettlementBatch({ batchId: detailFor, page: current, limit: PER_REQUEST });
        const payload = _.get(response, 'data.lines') || {};
        collected.push(...(payload.results || []));
        pages = payload.totalPages || 1;
        current += 1;
      } while (current <= pages);

      if (_.isEmpty(collected)) {
        toast.error(t('cpoSettlement.downloadEmpty'));
        return;
      }

      const rows = collected.map((line) => ({
        'Session ID': _.get(line, 'session_id') || _.get(line, 'booking.id') || '',
        'Charger ID (OCPI)': _.get(line, 'external_charger_id') || '',
        'Station ID (OCPI)': _.get(line, 'external_station_id') || '',
        'Station Name': _.get(line, 'external_station_name') || '',
        'Session End': _.get(line, 'booking.booking_stop') ? moment(line.booking.booking_stop).format('DD/MM/YYYY HH:mm') : '',
        'Partner Session End': _.get(line, 'cdr_end_date_time') ? moment(line.cdr_end_date_time).format('DD/MM/YYYY HH:mm') : '',
        'Amount (INR)': _.get(line, 'amount', 0),
        Status: _.get(line, 'status') || '',
        UTR: _.get(line, 'utr') || '',
      }));

      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = { Sheets: { sessions: sheet }, SheetNames: ['sessions'] };
      const buffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });
      FileSaver.saveAs(blob, `${_.get(batchDetail, 'batch_id') || 'cpo-settlement'}-sessions.xlsx`);
    } catch (error) {
      // Surfaced rather than swallowed: a silent no-op looks identical to a
      // settlement that genuinely holds no sessions.
      toast.error(_.get(error, 'response.data.message') || t('cpoSettlement.downloadFailed'));
    } finally {
      setIsDownloading(false);
    }
  }, [detailFor, batchDetail, t]);

  /**
   * Ask the provider where this one payout stands, now.
   *
   * The list is refetched on the way back rather than patched in place, so the row
   * reflects whatever the server stored — including a status this screen does not
   * derive for itself, such as a reversal.
   */
  const refreshBatch = useCallback(
    (batch) => {
      dispatch({
        type: REFRESH_CPO_SETTLEMENT_BATCH.REQUEST,
        payload: batch.id,
        cb: () => fetchBatches({ page: pageNo }),
      });
    },
    [dispatch, fetchBatches, pageNo]
  );

  // react-paginate hands the callback an OBJECT ({ selected: n }), not a number.
  // Treating it as a number produced "[object Object]1" as the page and the server
  // rejected it with "page must be a number". `.selected + 1` is the convention
  // used by every other list screen in this app.
  const handlePageClick = useCallback(
    (pageInfo) => {
      const nextPage = pageInfo.selected + 1;
      setPageNo(nextPage);
      fetchBatches({ page: nextPage });
    },
    [fetchBatches]
  );

  // Every action re-fetches the list on success rather than mutating the row
  // locally: the server decides the resulting status, and guessing it here would
  // eventually disagree with what actually happened.
  const refresh = () => fetchBatches({ page: pageNo });

  /**
   * Opening any modal locks body scrolling, and because the page itself is the
   * scroll container that drops the reader back to the top of a long table. The
   * position is captured on the way in and restored on the way out, so acting on a
   * row further down the list leaves you where you were.
   */
  const scrollRef = React.useRef(0);

  const rememberScroll = () => {
    scrollRef.current = window.scrollY || document.documentElement.scrollTop || 0;
  };

  const restoreScroll = () => {
    // After the modal has actually unmounted and body scrolling is released;
    // scrolling any earlier is undone by the release itself.
    window.requestAnimationFrame(() => window.scrollTo({ top: scrollRef.current, behavior: 'auto' }));
  };

  const LINES_PER_PAGE = 10;

  const fetchLines = (batchId, linePage) =>
    dispatch({
      type: GET_CPO_SETTLEMENT_BATCH.REQUEST,
      payload: { batchId, limit: LINES_PER_PAGE, page: linePage },
    });

  const openDetail = (batchId) => {
    rememberScroll();
    setDetailFor(batchId);
    setShowDetail(true);
    fetchLines(batchId, 1);
  };

  // Paging the breakdown keeps the same batch open — only its lines change.
  // Same object-not-number contract as the list pagination above.
  const handleLinePageClick = (pageInfo) => {
    if (detailFor) {
      fetchLines(detailFor, pageInfo.selected + 1);
    }
  };

  // Both of these send money, so both are confirmed against the partner and amount
  // rather than acting on a single click.
  const openConfirm = (batch, kind) => {
    rememberScroll();
    setConfirmFor({ batch, kind });
  };

  const submitConfirm = () => {
    const kind = _.get(confirmFor, 'kind');
    dispatch({
      type: kind === 'approve' ? APPROVE_CPO_SETTLEMENT_BATCH.REQUEST : PAY_CPO_SETTLEMENT_BATCH.REQUEST,
      payload: _.get(confirmFor, 'batch.id'),
      cb: () => {
        setConfirmFor(null);
        refresh();
      },
    });
  };


  // The status travels with the id because releasing a settlement under review is
  // a materially different act from cancelling one that was never sent, and the
  // dialog has to say so.
  const openReject = (batch) => {
    rememberScroll();
    setRejectFor({ id: batch.id, status: _.get(batch, 'status', '') });
  };

  const isReleasingReview = _.get(rejectFor, 'status') === 'needs_review';

  const submitReject = () => {
    dispatch({
      type: REJECT_CPO_SETTLEMENT_BATCH.REQUEST,
      payload: { batchId: _.get(rejectFor, 'id'), reason: rejectReason },
      cb: () => {
        setRejectFor(null);
        setRejectReason('');
        refresh();
      },
    });
  };

  const refreshPartners = () => dispatch({ type: FETCH_CPO.REQUEST, payload: { limit: 999 } });

  /**
   * Generate settlements for one partner, or deliberately for all of them.
   *
   * Targeted by default. A run creates the settlement records; it does not pay
   * anything — each resulting row is then reviewed and actioned on its own. Running
   * for every partner at once is still possible because that is what the scheduled
   * daily job does, but it has to be chosen explicitly.
   */
  const submitRun = () => {
    dispatch({
      type: RUN_CPO_SETTLEMENT_CYCLE.REQUEST,
      payload: runPartner && runPartner !== RUN_ALL_PARTNERS ? { ocpiCredentialId: runPartner } : {},
      cb: () => {
        setShowRun(false);
        setRunPartner('');
        refresh();
      },
    });
  };

  const openConfig = (partnerId = null) => {
    rememberScroll();
    setEditPartnerId(partnerId);
    setShowConfig(true);
  };

  const closeConfig = () => {
    setShowConfig(false);
    setEditPartnerId(null);
  };

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={t('cpoSettlement.title')} />
        <div className="page-content-wrapper scrollable">
          <div className="settlement-list-page">
            <Card>
              <div className="settlement-list-page__inner">
                <div className="settlement-list-top__block">
                  <Row className="settlement-list-search__block">
                    <Col xl={12} md={12} className="settlement-list-run__block">
                      <Button className="settlement-run-btn settlement-run-btn--secondary" onClick={() => openConfig(null)}>
                        {t('cpoSettlement.configure')}
                      </Button>
                      <Button
                        className="settlement-run-btn"
                        onClick={() => {
                          rememberScroll();
                          setShowRun(true);
                        }}
                        disabled={isActionLoading}
                      >
                        {t('cpoSettlement.runNow')}
                      </Button>
                    </Col>
                  </Row>
                </div>

                {/* Which partners are set up, and the settings that decide what a
                    run will do for each of them. Without this the only way to tell
                    whether a partner was ever configured was to open the form and
                    look. */}
                <div className="settlement-partners__block">
                  <div className="settlement-partners__head">
                    <div className="settlement-partners__title">{t('cpoSettlement.configuredPartners')}</div>
                    <div className="settlement-partners__count">
                      {configuredPartners.length} {t('cpoSettlement.configuredCount')}
                    </div>
                  </div>
                  {_.isEmpty(configuredPartners) ? (
                    <div className="settlement-partners__empty">{t('cpoSettlement.noConfiguredPartners')}</div>
                  ) : (
                    <div className="settlement-partners__list">
                      {configuredPartners.map((partner) => {
                        const partnerConfig = _.get(partner, 'cpo_settlement_config') || {};
                        const isLive = partnerConfig.run_mode === 'live';
                        return (
                          <div className="settlement-partner-card" key={`configured-partner-${partner.id}`}>
                            <div className="settlement-partner-card__top">
                              <div className="settlement-partner-card__name">{partnerLabel(partner)}</div>
                              <Button className="settlement-action-btn" onClick={() => openConfig(partner.id)}>
                                {t('cpoSettlement.edit')}
                              </Button>
                            </div>
                            <div className="settlement-partner-card__top">
                              <span className={`settlement-status ${isLive ? 'settlement-status--success' : 'settlement-status--muted'}`}>
                                {isLive ? t('cpoSettlement.livePayments') : t('cpoSettlement.verifyOnly')}
                              </span>
                            </div>
                            <dl className="settlement-partner-card__meta">
                              <dt>{t('cpoSettlement.cycle')}</dt>
                              <dd>{`T+${partnerConfig.settlement_frequency_days ?? '-'}`}</dd>
                              <dt>{t('cpoSettlement.startDate')}</dt>
                              <dd>
                                {partnerConfig.settlement_start_date ? moment(partnerConfig.settlement_start_date).format('DD/MM/YYYY') : '-'}
                              </dd>
                              <dt>{t('cpoSettlement.approvalAbove')}</dt>
                              {/* Always applies now — the cap is the only control,
                                  so there is no "not required" case. */}
                              <dd>{formatAmount(Number(partnerConfig.max_cap))}</dd>
                              <dt>{t('cpoSettlement.bankAccount')}</dt>
                              <dd>{partner.bankName ? `${partner.bankName} · ${maskAccount(partner.account_no)}` : maskAccount(partner.account_no)}</dd>
                              <dt>{t('cpoSettlement.lastRun')}</dt>
                              <dd>{partnerConfig.last_settlement_run_at ? moment(partnerConfig.last_settlement_run_at).format('DD/MM/YYYY HH:mm') : '-'}</dd>
                            </dl>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Directly above the table, because these filter the table and
                    nothing else. At the top of the page they read as filtering the
                    configured-partners panel too, which they never did. */}
                <Row className="settlement-filters__row">
                  <Col xl={3} lg={4} md={6}>
                    <Select
                      label={t('cpoSettlement.filterStatus')}
                      placeholder={t('cpoSettlement.filterStatus')}
                      value={statusFilter}
                      options={STATUS_OPTIONS}
                      onChange={(value) => setStatusFilter(value)}
                    />
                  </Col>
                  <Col xl={3} lg={4} md={6}>
                    <TextInput
                      type="date"
                      label={t('cpoSettlement.filterFrom')}
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </Col>
                  <Col xl={3} lg={4} md={6}>
                    <TextInput
                      type="date"
                      label={t('cpoSettlement.filterTo')}
                      // Bounded by the other end of the range, so an inverted
                      // window cannot be entered and silently return nothing.
                      min={dateFrom || undefined}
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </Col>
                  <Col xl={3} lg={12} md={6} className="settlement-filters__actions">
                    {/* Only rendered while something is actually filtered —
                        otherwise it would be a control that does nothing. */}
                    {hasFilters && (
                      <Button
                        className="settlement-run-btn settlement-run-btn--secondary settlement-filter__clear"
                        onClick={clearFilters}
                        title={t('cpoSettlement.clearFilter')}
                      >
                        <MdClear size={18} />
                        {t('button.clear')}
                      </Button>
                    )}
                  </Col>
                </Row>

                <div className="settlement-list-table">
                  <table className="record-list-table">
                    <thead>
                      <tr>
                        <th>{t('cpoSettlement.partner')}</th>
                        <th>{t('cpoSettlement.cycle')}</th>
                        <th>{t('cpoSettlement.settlementDate')}</th>
                        <th>{t('cpoSettlement.sessions')}</th>
                        <th>{t('cpoSettlement.amount')}</th>
                        <th>{t('cpoSettlement.utr')}</th>
                        <th>{t('cpoSettlement.status')}</th>
                        <th>{t('cpoSettlement.remaining')}</th>
                        <th>{t('cpoSettlement.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoader ? (
                        <tr>
                          <td colSpan={9}>
                            <SkeletonTheme baseColor="#1b1b1b" highlightColor="#333">
                              <Skeleton height={15} count={10} />
                            </SkeletonTheme>
                          </td>
                        </tr>
                      ) : _.isEmpty(batches) ? (
                        <tr>
                          <td colSpan={9} className="border-0">
                            <div className="empty-data-block">{t('cpoSettlement.noData')}</div>
                          </td>
                        </tr>
                      ) : (
                        batches.map((batch, index) => {
                          const status = _.get(batch, 'status', '');
                          const isVerifyOnly = _.get(batch, 'run_mode') === 'compute_only';
                          return (
                            <tr key={`settlement-batch-${index}`}>
                              {/* partner_name leads: `name` is never populated in
                                  either environment (0/132 prod, 0/219 UAT). */}
                              <td>
                                {_.get(batch, 'ocpiCredential.partner_name') ||
                                  _.get(batch, 'ocpiCredential.name') ||
                                  _.get(batch, 'ocpiCredential.partyId') ||
                                  _.get(batch, 'ocpiCredential.roles[0].business_details.name') ||
                                  '-'}
                              </td>
                              <td>{`T+${_.get(batch, 'cycle_days', '-')}`}</td>
                              <td>{batch.settlement_date ? moment(batch.settlement_date).format('DD/MM/YYYY') : '-'}</td>
                              <td>
                                {_.get(batch, 'session_count', 0)}
                                {/* A payment can legitimately cover more than one
                                    day when it sweeps up late-qualifying sessions.
                                    Shown so an unusually large total is
                                    explainable rather than alarming. */}
                                {_.get(batch, 'catch_up_session_count', 0) > 0 && (
                                  <span className="settlement-catchup">{` (+${batch.catch_up_session_count} ${t('cpoSettlement.catchUp')})`}</span>
                                )}
                              </td>
                              <td>{formatAmount(_.get(batch, 'total_amount'))}</td>
                              <td>{_.get(batch, 'utr') || '-'}</td>
                              <td>
                                {/* Status always comes from STATUS_LABELS, which is
                                    the same source the filter dropdown is built
                                    from — a row and the filter must never name the
                                    same status differently. The auto-versus-manual
                                    distinction is carried by the tag beside it, not
                                    by renaming the status. */}
                                <span className={`settlement-status ${STATUS_CLASS[status] || ''}`}>{STATUS_LABELS[status] || status}</span>
                                {isVerifyOnly && <span className="settlement-verify-tag">{t('cpoSettlement.manualTag')}</span>}
                                {/* The provider's own words on why it did not go through.
                                    Shown on the row rather than left in the database: without
                                    it "Failed" is unactionable, and the difference between
                                    "invalid account number" and a transient gateway error is
                                    the difference between fixing the partner's details and
                                    simply paying again. */}
                                {_.get(batch, 'failure_reason') && (
                                  <div className="settlement-status__reason" title={batch.failure_reason}>
                                    {batch.failure_reason}
                                  </div>
                                )}
                              </td>
                              <td>
                                {_.get(batch, 'carried_forward_session_count', 0) > 0
                                  ? `${batch.carried_forward_session_count} / ${formatAmount(_.get(batch, 'carried_forward_amount'))}`
                                  : '-'}
                              </td>
                              <td>
                                <div className="settlement-actions">
                                  <Button
                                    className="settlement-action-btn settlement-action-btn--icon"
                                    onClick={() => openDetail(batch.id)}
                                    title={t('cpoSettlement.viewSessions')}
                                  >
                                    <BsEye />
                                  </Button>
                                  {status === 'awaiting_approval' && (
                                    <React.Fragment>
                                      <Button className="settlement-action-btn" disabled={isActionLoading} onClick={() => openConfirm(batch, 'approve')}>
                                        {t('cpoSettlement.approveAndPay')}
                                      </Button>
                                      <Button
                                        className="settlement-action-btn settlement-action-btn--danger"
                                        disabled={isActionLoading}
                                        onClick={() => openReject(batch)}
                                      >
                                        {t('cpoSettlement.reject')}
                                      </Button>
                                    </React.Fragment>
                                  )}
                                  {/* Without this a settlement parked for review
                                      would hold its sessions permanently — never
                                      paid, and never released to a later run. */}
                                  {status === 'needs_review' && (
                                    <Button
                                      className="settlement-action-btn settlement-action-btn--danger"
                                      disabled={isActionLoading}
                                      onClick={() => openReject(batch)}
                                      title={t('cpoSettlement.releaseHint')}
                                    >
                                      {t('cpoSettlement.release')}
                                    </Button>
                                  )}
                                  {/* Payable either way. A settlement created outside
                                      automatic mode is not unpayable — it just has
                                      to be paid deliberately, which is this button.
                                      Reject sits alongside it so its sessions can
                                      always be released instead; without that a
                                      manual settlement nobody pays would hold its
                                      sessions permanently. */}
                                  {status === 'pending' && (
                                    <React.Fragment>
                                      <Button className="settlement-action-btn" disabled={isActionLoading} onClick={() => openConfirm(batch, 'pay')}>
                                        {t('cpoSettlement.settleNow')}
                                      </Button>
                                      <Button
                                        className="settlement-action-btn settlement-action-btn--danger"
                                        disabled={isActionLoading}
                                        onClick={() => openReject(batch)}
                                      >
                                        {t('cpoSettlement.reject')}
                                      </Button>
                                    </React.Fragment>
                                  )}
                                  {/* Only while the payout is actually in flight. The scheduled poll runs
                                      every 30 minutes, so a settlement paid moments ago keeps reading
                                      "processing" until then — this asks the bank now, for someone who
                                      does not want to wait. Nothing else can be refreshed: a terminal
                                      batch has no payout left to ask about. */}
                                  {status === 'processing' && (
                                    <Button
                                      className="settlement-action-btn settlement-action-btn--icon"
                                      disabled={refreshingBatchId === batch.id}
                                      onClick={() => refreshBatch(batch)}
                                      title={t('cpoSettlement.refreshStatus')}
                                    >
                                      {refreshingBatchId === batch.id ? <Spinner animation="border" size="sm" /> : <MdRefresh size={18} />}
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
            {!isLoader && !_.isEmpty(batches) && (
              <ReactPagination
                currentPage={page}
                limit={10}
                total={totalRecords}
                handlePageClick={handlePageClick}
                totalPages={totalPages}
                marginPagesDisplayed={1}
              />
            )}
          </div>
          <Footer />
        </div>
      </LayoutContainer>

      <CpoSettlementConfigModal
        show={showConfig}
        partnerId={editPartnerId}
        onClose={closeConfig}
        onExited={restoreScroll}
        onSaved={() => {
          // The panel above reads from the partner list, so it has to be re-fetched
          // too — otherwise a just-saved change is invisible until a page reload.
          refreshPartners();
          fetchBatches({ page: pageNo });
        }}
      />

      {/* Generating a settlement is per-partner by default: each partner's figures
          are reviewed on their own row before anything is paid. */}
      <Modal show={showRun} centered onHide={() => setShowRun(false)} onExited={restoreScroll}>
        <div className="settlement-detail-modal">
          <div className="settlement-detail-modal__header">
            <div className="settlement-detail-modal__title">{t('cpoSettlement.runTitle')}</div>
            <MdClose size={28} onClick={() => setShowRun(false)} className="cursor-pointer" />
          </div>
          <div className="settlement-detail-modal__body">
            <p className="settlement-run-modal__note">{t('cpoSettlement.runNote')}</p>
            <Select
              isRequired
              label={t('cpoSettlement.runFor')}
              placeholder={t('cpoSettlement.runForPlaceholder')}
              value={runPartner}
              options={runPartnerOptions}
              onChange={(value) => setRunPartner(value)}
            />
            {/* This button runs the same cycle as the scheduler, which means it can
                send money. Said plainly before the click, not after. */}
            {runPartner && <div className="settlement-reconcile-warning">{t('cpoSettlement.runPayWarning')}</div>}
          </div>
          <div className="settlement-detail-modal__actions">
            <Button className="settlement-action-btn" onClick={() => setShowRun(false)}>
              {t('button.cancel')}
            </Button>
            <Button className="settlement-action-btn" disabled={!runPartner || isActionLoading} onClick={submitRun}>
              {t('cpoSettlement.runConfirm')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Session breakdown for one payment */}
      <Modal show={showDetail} size="lg" centered onHide={() => setShowDetail(false)} onExited={restoreScroll}>
        <div className="settlement-detail-modal">
          <div className="settlement-detail-modal__header">
            <div className="settlement-detail-modal__title">
              {t('cpoSettlement.detailTitle')}
              {batchDetail ? ` — ${_.get(batchDetail, 'batch_id', '')}` : ''}
              {/* Stated explicitly so a paged breakdown can never be mistaken for
                  the whole of it. */}
              {linesTotal > 0 && <span className="settlement-detail-modal__count">{` (${linesTotal} ${t('cpoSettlement.sessions')})`}</span>}
            </div>
            <div className="settlement-detail-modal__head-actions">
              {/* Exports every session in the settlement, not just the visible page —
                  the point of the file is to reconcile the whole payment against a
                  partner statement. */}
              <Button
                className="settlement-action-btn settlement-action-btn--secondary"
                disabled={isDownloading || !linesTotal}
                onClick={downloadSessions}
                title={t('cpoSettlement.downloadSessions')}
              >
                {isDownloading ? <Spinner animation="border" size="sm" /> : <MdFileDownload size={18} />}
                {t('button.download')}
              </Button>
              <MdClose size={28} onClick={() => setShowDetail(false)} className="cursor-pointer" />
            </div>
          </div>
          <div className="settlement-detail-modal__body">
            {/* A spinner rather than the previous row's figures. The reducer clears
                the old settlement as the request goes out, so there is nothing
                stale to show and nothing that could be mistaken for this one. */}
            {isDetailLoading ? (
              <div className="settlement-detail-loader">
                <Spinner animation="border" role="status" />
                <span>{t('cpoSettlement.loadingSessions')}</span>
              </div>
            ) : (
              <React.Fragment>
                {/* The server tells us whether the lines sum to the payment amount.
                    Surfaced because a mismatch means the payment does not match its
                    own breakdown, which finance must see immediately. */}
                {reconciles === false && <div className="settlement-reconcile-warning">{t('cpoSettlement.reconcileWarning')}</div>}
                <table className="record-list-table">
                  <thead>
                    <tr>
                      <th>{t('cpoSettlement.sessionId')}</th>
                      {/* The partner's own charge point and location references. Shown here as
                          well as in the export, because matching a disputed session against a
                          partner statement starts with "which charger was this". */}
                      <th>{t('cpoSettlement.chargerIdOcpi')}</th>
                      <th>{t('cpoSettlement.stationOcpi')}</th>
                      <th>{t('cpoSettlement.sessionEnd')}</th>
                      <th>{t('cpoSettlement.partnerSessionEnd')}</th>
                      <th>{t('cpoSettlement.amount')}</th>
                      <th>{t('cpoSettlement.status')}</th>
                      <th>{t('cpoSettlement.utr')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {_.isEmpty(batchLines) ? (
                      <tr>
                        <td colSpan={8} className="border-0">
                          <div className="empty-data-block">{t('cpoSettlement.noSessions')}</div>
                        </td>
                      </tr>
                    ) : (
                      batchLines.map((line, index) => (
                        <tr key={`settlement-line-${index}`}>
                          <td>{_.get(line, 'session_id') || _.get(line, 'booking.id') || '-'}</td>
                          <td>{_.get(line, 'external_charger_id') || '-'}</td>
                          {/* Name first because that is what identifies the site to a person;
                              the id stays beneath it, since that is the reference a partner
                              statement is reconciled against. */}
                          <td>
                            {_.get(line, 'external_station_name') || _.get(line, 'external_station_id') || '-'}
                            {_.get(line, 'external_station_name') && _.get(line, 'external_station_id') ? (
                              <div className="settlement-cell__secondary">{line.external_station_id}</div>
                            ) : null}
                          </td>
                          <td>{_.get(line, 'booking.booking_stop') ? moment(line.booking.booking_stop).format('DD/MM/YYYY HH:mm') : '-'}</td>
                          {/* The partner's own session end time. A session repaired
                              after a fault carries our repair timestamp, so these two
                              can differ — showing both is what lets a partner
                              statement be matched line by line. */}
                          <td>{_.get(line, 'cdr_end_date_time') ? moment(line.cdr_end_date_time).format('DD/MM/YYYY HH:mm') : '-'}</td>
                          <td>{formatAmount(_.get(line, 'amount'))}</td>
                          <td>{_.get(line, 'status', '-')}</td>
                          <td>{_.get(line, 'utr') || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {/* A batch can hold more sessions than one page. Without this the
                    breakdown showed only the first page while looking complete,
                    so the visible lines did not add up to the amount paid. */}
                {linesTotalPages > 1 && (
                  <ReactPagination
                    currentPage={linesPage}
                    limit={LINES_PER_PAGE}
                    total={linesTotal}
                    handlePageClick={handleLinePageClick}
                    totalPages={linesTotalPages}
                    marginPagesDisplayed={1}
                  />
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      </Modal>

      {/* Both approving and paying send money, so the partner, amount and
          destination account are shown first — a misclick must not be able to send
          five figures. */}
      <Modal show={!!confirmFor} centered onHide={() => setConfirmFor(null)} onExited={restoreScroll}>
        <div className="settlement-detail-modal">
          <div className="settlement-detail-modal__header">
            <div className="settlement-detail-modal__title">
              {_.get(confirmFor, 'kind') === 'approve' ? t('cpoSettlement.approveTitle') : t('cpoSettlement.payTitle')}
            </div>
            <MdClose size={28} onClick={() => setConfirmFor(null)} className="cursor-pointer" />
          </div>
          <div className="settlement-detail-modal__body">
            <dl className="settlement-confirm__meta">
              <dt>{t('cpoSettlement.partner')}</dt>
              <dd>{partnerLabel(_.get(confirmFor, 'batch.ocpiCredential'))}</dd>
              <dt>{t('cpoSettlement.amount')}</dt>
              <dd className="settlement-confirm__amount">{formatAmount(_.get(confirmFor, 'batch.total_amount'))}</dd>
              <dt>{t('cpoSettlement.sessions')}</dt>
              <dd>{_.get(confirmFor, 'batch.session_count', 0)}</dd>
              <dt>{t('cpoSettlement.bankAccount')}</dt>
              <dd>
                {_.get(confirmFor, 'batch.beneficiary_snapshot.bank_name') || _.get(confirmFor, 'batch.ocpiCredential.bankName') || '-'}
                {' · '}
                {maskAccount(
                  _.get(confirmFor, 'batch.beneficiary_snapshot.account_no') || _.get(confirmFor, 'batch.ocpiCredential.account_no')
                )}
              </dd>
            </dl>
            <div className="settlement-reconcile-warning">{t('cpoSettlement.approveWarning')}</div>
          </div>
          <div className="settlement-detail-modal__actions">
            <Button className="settlement-action-btn" onClick={() => setConfirmFor(null)}>
              {t('button.cancel')}
            </Button>
            <Button className="settlement-action-btn" disabled={isActionLoading} onClick={submitConfirm}>
              {_.get(confirmFor, 'kind') === 'approve' ? t('cpoSettlement.approveConfirm') : t('cpoSettlement.payConfirm')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Rejecting releases the sessions, so a reason is worth capturing */}
      <Modal show={!!rejectFor} centered onHide={() => setRejectFor(null)} onExited={restoreScroll}>
        <div className="settlement-detail-modal">
          <div className="settlement-detail-modal__header">
            <div className="settlement-detail-modal__title">
              {isReleasingReview ? t('cpoSettlement.releaseTitle') : t('cpoSettlement.rejectTitle')}
            </div>
            <MdClose size={28} onClick={() => setRejectFor(null)} className="cursor-pointer" />
          </div>
          <div className="settlement-detail-modal__body">
            {/* Releasing a settlement under review is the only action here that
                could result in a second payment, so it is warned about rather than
                worded like an ordinary cancellation. */}
            {isReleasingReview ? (
              <div className="settlement-reconcile-warning">{t('cpoSettlement.releaseWarning')}</div>
            ) : (
              <p>{t('cpoSettlement.rejectNote')}</p>
            )}
            <TextInput
              isRequired={isReleasingReview}
              label={isReleasingReview ? t('cpoSettlement.releaseReason') : t('cpoSettlement.rejectReason')}
              placeholder={isReleasingReview ? t('cpoSettlement.releaseReasonPlaceholder') : t('cpoSettlement.rejectReason')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="settlement-detail-modal__actions">
              <Button className="settlement-action-btn" onClick={() => setRejectFor(null)}>
                {t('button.cancel')}
              </Button>
              <Button
                className="settlement-action-btn settlement-action-btn--danger"
                // The server also enforces this; disabling here means the user is
                // told before the request rather than by an error afterwards.
                disabled={isActionLoading || (isReleasingReview && !rejectReason.trim())}
                onClick={submitReject}
              >
                {isReleasingReview ? t('cpoSettlement.release') : t('cpoSettlement.reject')}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default SettlementList;
